import { Inject, Injectable } from '@angular/core';
import { Auth, AuthState } from '@freelancer/auth';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ErrorCodeApi } from 'api-typings/errors/errors';
import * as Rx from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  publishReplay,
  refCount,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { Collections, LegacySearchCollections } from '../collections.model';
import { CollectionActions, RequestDataPayload, TypedAction } from './actions';
import { BackendPushResponse, StoreBackend } from './backend';
import { DatastoreCollection } from './datastore-collection';
import { DatastoreDocument } from './datastore-document';
import { DatastoreSearchCollection } from './datastore-search-collection';
import { LOGGED_OUT_KEY } from './datastore.interface';
import {
  arrayIsShallowEqual,
  isDefined,
  objectFilterByKeys,
  sameElements,
  setDiff,
  toNumber,
  toObservable,
} from './helpers';
import { DatastoreMissingModuleError } from './missing-module-error';
import { duplicateFirst } from './operators/duplicateFirst';
import { select } from './operators/select';
import { DocumentQuery, NullQuery, Query } from './query';
import { RequestDataConfig, REQUEST_DATA_CONFIG } from './request-data';
import {
  requestStatusesEqual,
  RequestStatusHandler,
} from './request-status-handler.service';
import { SearchManager } from './search';
import {
  flattenQuery,
  isSearchQuery,
  selectEntitiesForQuery,
} from './store.helpers';
import {
  Path,
  QueryParams,
  Reference,
  SearchQueryParams,
  StoreState,
} from './store.model';
import { WebSocketService } from './websocket';

@Injectable()
export class Datastore {
  constructor(
    private store$: Store<StoreState>,
    private action$: Actions<TypedAction>,
    private storeBackend: StoreBackend,
    private webSocketService: WebSocketService,
    private searchManager: SearchManager,
    private requestStatusHandler: RequestStatusHandler,
    private auth: Auth,
    @Inject(REQUEST_DATA_CONFIG) private requestDataConfig: RequestDataConfig,
  ) {}

  /**
   * Returns a collection of objects, customised by query.
   *
   * @param collectionName Collection name
   * @param queryFn Query to refine results
   */
  collection<K extends keyof Collections>(
    collectionName: K,
    queryFn?: (q: Query<K>) => Query<K> | Rx.Observable<Query<K> | NullQuery>,
  ): DatastoreCollection<K> {
    const flattenedQuery$ = flattenQuery(queryFn);

    const refStream$: Rx.Observable<Reference<K>> = Rx.combineLatest([
      toObservable(collectionName),
      toObservable(this.auth.authState$),
      flattenedQuery$,
    ]).pipe(
      map(
        ([
          collection,
          authState,
          { limit, queryParams, searchQueryParams, order },
        ]) => ({
          path: {
            collection,
            authUid: authState ? authState.userId : LOGGED_OUT_KEY,
          },
          query: {
            limit,
            queryParams,
            searchQueryParams,
            isDocumentQuery: false,
          },
          order,
        }),
      ),
    );

    // Requests to the backend, further processed in `request-data.effect.ts`
    const requestStream$: Rx.Observable<
      RequestDataPayload<K>
    > = refStream$.pipe(
      map(ref => {
        const request: RequestDataPayload<K> = {
          type: ref.path.collection,
          ref,
          clientRequestIds: [this.generateClientRequestId()],
        };

        return request;
      }),
      tap(request => {
        // Only dispatch to store on a non-null query
        if (
          // FIXME: Make query a tagged union
          request.ref.query &&
          (request.ref.query.queryParams || isSearchQuery(request.ref.query))
        ) {
          const action: CollectionActions<K> = {
            type: 'REQUEST_DATA',
            payload: request,
          };
          this.store$.dispatch(action);
        }
      }),
      publishReplay(1),
      refCount(),
    );

    const sourceStream$ = requestStream$.pipe(
      tap(request =>
        this.requestStatusHandler.update(request, {
          ready: false,
        }),
      ),
      switchMap(request => {
        const {
          ref,
          ref: {
            path: { collection, authUid },
            query,
          },
        } = request;

        const defaultOrder = this.storeBackend.defaultOrder(collection);
        const source$ = // grab the collection items from the store
          // FIXME: Make query a tagged union
          query && !query.queryParams && !isSearchQuery(query)
            ? Rx.of([]) // immediately emit on null query
            : this.store$.pipe(
                // store$ emits on every action dispatched and store state change
                tap(source => this.verifyModuleIsImported(collection, source)), // TODO: T75102
                select(collection, authUid),
                filter(isDefined),
                map(storeSlice =>
                  selectEntitiesForQuery(storeSlice, ref, defaultOrder),
                ),
                filter(isDefined),
              );
        return source$.pipe(
          tap(() =>
            // Update request status here on fetching from either network or cache
            this.requestStatusHandler.update(request, {
              ready: true,
            }),
          ),
          distinctUntilChanged(arrayIsShallowEqual),
        );
      }),
      publishReplay(1),
      refCount(),
    );

    const statusStream$ = Rx.combineLatest([
      requestStream$.pipe(
        switchMap(request => this.requestStatusHandler.get$(request)),
      ),
      // combining with sourceStream to set ready flag to false on initial subscribe
      sourceStream$.pipe(startWith<any>(undefined)),
    ]).pipe(
      map(([status]) => status),
      distinctUntilChanged(requestStatusesEqual),
      publishReplay(1),
      refCount(),
    );

    return new DatastoreCollection(
      refStream$,
      this.storeBackend,
      statusStream$,
      Rx.combineLatest([
        statusStream$.pipe(startWith<any>(undefined)),
        sourceStream$,
      ]).pipe(
        map(([, source]) => source),
        distinctUntilChanged(),
      ),
      Rx.NEVER, // FIXME
    );
  }

  /**
   * Returns a single document from a collection specified by id.
   * If `documentId` is an observable, new objects may be fetched from the
   * network and emitted as they arrive. Note that if it emits too quickly,
   * such that the network request has not completed before the id changes,
   * that object with the previous id will not be emitted.
   *
   * @param collectionName Collection name
   * @param documentId$ If not provided, defaults to the first document of the collection.
   * Omitting this is useful for logged-out requests when document ID is not applicable
   * @param query Only required if querying by a unique secondary ID
   */
  document<K extends keyof Collections>(
    collectionName: K,
    documentId$?:
      | string
      | number
      | Rx.Observable<string>
      | Rx.Observable<number>,
  ): DatastoreDocument<K>;
  document<K extends keyof Collections, OtherId extends keyof Collections[K]>(
    collectionName: K,
    documentSecondaryId$:
      | Collections[K][OtherId]
      | Rx.Observable<Collections[K][OtherId]>,
    documentQuery$:
      | DocumentQuery<K, OtherId>
      | Rx.Observable<DocumentQuery<K, OtherId> | undefined>,
  ): DatastoreDocument<K>;

  document<K extends keyof Collections, OtherId extends keyof Collections[K]>(
    collectionName: K,
    documentId$?:
      | string
      | number
      | Rx.Observable<string>
      | Rx.Observable<number>,
    documentQuery$?:
      | DocumentQuery<K, OtherId>
      | Rx.Observable<DocumentQuery<K, OtherId> | undefined>,
  ): DatastoreDocument<K> {
    const refStream$: Rx.Observable<Reference<K>> = Rx.combineLatest([
      toObservable(collectionName),
      toObservable(this.auth.authState$),
      toObservable<number | string | undefined>(documentId$).pipe(
        distinctUntilChanged(),
      ),
      toObservable(documentQuery$).pipe(
        distinctUntilChanged(
          (a, b) =>
            !!a &&
            !!b &&
            a.caseInsensitive === b.caseInsensitive &&
            a.index === b.index,
        ),
      ),
    ]).pipe(
      map(([collection, authState, id, documentQuery]) => {
        const path = {
          collection,
          authUid: authState ? authState.userId : LOGGED_OUT_KEY,
        };

        if (!id) {
          // Treat as a document query with no params
          return {
            path,
            query: {
              isDocumentQuery: true,
              limit: 1,
              queryParams: {},
              searchQueryParams: {},
            },
          };
        }

        return documentQuery
          ? {
              path,
              query: {
                isDocumentQuery: true,
                limit: 1,
                queryParams: {
                  [documentQuery.index]: [
                    {
                      name: documentQuery.index,
                      condition: documentQuery.caseInsensitive
                        ? 'equalsIgnoreCase'
                        : '==',
                      value: id,
                    } as unknown,
                  ],
                } as QueryParams<Collections[K]>,
                searchQueryParams: {},
              },
            }
          : {
              path: {
                ...path,
                ids: [id.toString()],
              },
            };
      }),
    );

    const requestStream$ = refStream$.pipe(
      map(ref => ({
        type: ref.path.collection,
        ref,
        clientRequestIds: [this.generateClientRequestId()],
      })),
      tap(request => {
        const action = {
          type: 'REQUEST_DATA',
          payload: request,
        };
        this.store$.dispatch(action);
      }),
      publishReplay(1),
      refCount(),
    );

    // TODO: Rx.merge causes double emission - T53648
    const sourceStream$ = (collectionName === 'onlineOffline' // include websocket subscription stream
      ? Rx.merge(
          Rx.combineLatest([
            this.webSocketService.subscribeOnlineOffline(
              requestStream$.pipe(
                map(request => request.ref.path.ids),
                filter(isDefined),
              ),
            ),
            requestStream$,
          ]).pipe(map(([ss, req]) => req)),
          requestStream$,
        )
      : requestStream$
    ).pipe(
      tap(request =>
        this.requestStatusHandler.update(request, {
          ready: false,
        }),
      ),
      switchMap(request => {
        const {
          ref,
          ref: {
            path: { collection, authUid },
          },
        } = request;
        const document$ = this.store$.pipe(
          tap(source => this.verifyModuleIsImported(collection, source)), // TODO: T75102
          select(collection, authUid),
          filter(isDefined),
          map(state => this.fetchSingleObject(state, ref)),
        );

        return Rx.combineLatest([
          document$,
          this.action$.pipe(startWith(undefined)),
        ]).pipe(
          // Update request status here on fetching from either network or cache
          tap(([document, action]) => {
            // Because the missing items check in RequestDataEffect can fail
            // when requests are batched, this is a hacky way to check that the
            // item asked for in the request does not exist even after a
            // "successful" fetch.
            // TODO: If the document is cached but then deleted this check is
            // not sufficient - need to address other actions that mutate store
            if (
              action &&
              action.type === 'API_FETCH_SUCCESS' &&
              request.clientRequestIds.every(id =>
                action.payload.clientRequestIds.some(
                  actionId => actionId === id,
                ),
              ) &&
              document === undefined
            ) {
              // TODO: replace with ErrorHandler
              console.warn(
                `Fetch succeeded but object(s) not found from document call to '${request.type}'. Result was`,
                action.payload.result,
              );
              this.requestStatusHandler.update(request, {
                ready: false,
                error: {
                  errorCode: ErrorCodeApi.NOT_FOUND,
                },
              });
            }
          }),
          map(([document]) => document),
          filter(isDefined),
          tap(() => {
            this.requestStatusHandler.update(request, {
              ready: true,
            });
          }),
          distinctUntilChanged(),
        );
      }),
      publishReplay(1),
      refCount(),
    );

    const statusStream$ = Rx.combineLatest([
      requestStream$.pipe(
        switchMap(request => this.requestStatusHandler.get$(request)),
      ),
      // combining with sourceStream to set ready flag to false on initial subscribe
      sourceStream$.pipe(startWith<any>(undefined)),
    ]).pipe(
      map(([status]) => status),
      distinctUntilChanged(requestStatusesEqual),
      publishReplay(1),
      refCount(),
    );

    return new DatastoreDocument(
      refStream$,
      this.storeBackend,
      statusStream$,
      Rx.combineLatest([
        statusStream$.pipe(startWith<any>(undefined)),
        sourceStream$,
      ]).pipe(
        map(([, source]) => source),
        distinctUntilChanged(),
      ),
    );
  }

  private fetchSingleObject<K extends keyof Collections>(
    storeSlice: NonNullable<StoreState[K][string]>,
    ref: Reference<K>,
  ): Collections[K] | undefined {
    const {
      path: { collection, ids },
    } = ref;

    if (ids) {
      return storeSlice.entities[ids[0]] && storeSlice.entities[ids[0]].entity;
    }

    const defaultOrder = this.storeBackend.defaultOrder(collection);
    const entities = selectEntitiesForQuery(storeSlice, ref, defaultOrder);
    return entities ? entities[0] : undefined;
  }

  /**
   * Returns a list of objects from a collection specified by id.
   * If an observable is passed in for `objectIds` then only the delta will be
   * refetched from the network.
   *
   * @param collectionName Collection name
   * @param objectIds$
   */
  objects<K extends keyof Collections>(
    collectionName: K,
    objectIds$:
      | ReadonlyArray<number>
      | ReadonlyArray<string>
      | Rx.Observable<ReadonlyArray<number>>
      | Rx.Observable<ReadonlyArray<string>>,
  ): Rx.Observable<ReadonlyArray<Collections[K]>> {
    const requestStream$ = Rx.combineLatest([
      toObservable(collectionName),
      toObservable(this.auth.authState$),
      // FIXME: this should be ReadonlyArray<number> | ReadonlyArray<string>
      // https://github.com/Microsoft/TypeScript/issues/7294
      toObservable<ReadonlyArray<number | string>>(objectIds$).pipe(
        distinctUntilChanged(arrayIsShallowEqual),
      ),
    ]).pipe(
      map(([collection, authState, ids]) => {
        const path: { collection: K; authUid: string; ids: string[] } = {
          collection,
          authUid: authState ? authState.userId : LOGGED_OUT_KEY,
          ids: ids.map(id => id.toString()),
        };
        return { path };
      }),
      // Add an path at the start with an empty list of ids
      duplicateFirst<{
        path: { collection: K; authUid: string; ids: string[] };
      }>(({ path: { collection, authUid } }) => ({
        path: { collection, authUid, ids: [] },
      })),
      pairwise(), // Use pairwise to calculate and fetch new objects only
      switchMap(paths => {
        const [
          {
            path: { collection, authUid },
          },
        ] = paths;
        return this.store$.pipe(
          select(collection, authUid),
          take(1),
          map(data => ({ paths, data })),
        );
      }),
      map(
        ({
          paths: [
            {
              path: { ids: idsOrig },
            },
            {
              path: { collection, authUid, ids: idsNew },
            },
          ],
          data,
        }) => {
          // Do not refetch entities that have been fetched recently. Useful for
          // projections where multiple collections are populated with one
          // datastore call, e.g. `threads_get` with a `user_details` projection.
          // While projections exist this must co-exist with the dedupe in
          // RequestDataEffect, which is collection-specific
          const newIds = setDiff(idsNew, idsOrig).filter(id =>
            data &&
            data.entities[id] &&
            this.requestDataConfig.dedupeWindowTime > 0
              ? toNumber(data.entities[id].timeFetched) +
                  this.requestDataConfig.dedupeWindowTime <
                Date.now()
              : true,
          );
          const action = {
            type: 'REQUEST_DATA',
            payload: {
              type: collection,
              ref: { path: { collection, authUid, ids: newIds } },
              clientRequestIds: [this.generateClientRequestId()],
            },
          };
          if (newIds.length) {
            this.store$.dispatch(action);
          }
          return {
            ...action.payload,
            ...{ ref: { path: { collection, authUid, ids: idsNew } } },
          };
        },
      ),
      publishReplay(1),
      refCount(),
    );

    // FIXME: This will double emit for `collection === 'onlineOffline'` due to Rx.merge
    const sourceStream$ =
      collectionName === 'onlineOffline' // include websocket subscription stream
        ? Rx.merge(
            Rx.combineLatest([
              this.webSocketService.subscribeOnlineOffline(
                requestStream$.pipe(
                  map(
                    ({
                      ref: {
                        path: { collection, authUid, ids },
                      },
                    }) => ids,
                  ),
                ),
              ),
              requestStream$,
            ]).pipe(map(([ss, ref]) => ref)),
            requestStream$,
          )
        : requestStream$;

    return sourceStream$.pipe(
      switchMap(({ ref: { path: { collection, authUid, ids } } }) =>
        ids.length === 0
          ? Rx.of([])
          : this.store$.pipe(
              tap(store => this.verifyModuleIsImported(collection, store)), // TODO: T75102
              select(collection, authUid),
              filter(isDefined),
              map(entitiesObject =>
                objectFilterByKeys<
                  NonNullable<StoreState[K][string]>['entities'][string]
                >(entitiesObject.entities, ids),
              ),
              map(entitiesObject =>
                Object.keys(entitiesObject).length
                  ? Object.values(entitiesObject)
                  : undefined,
              ),
              filter(isDefined),
              map(entities => entities.map(entity => entity.entity)),
              distinctUntilChanged(sameElements),
            ),
      ),
      publishReplay(1),
      refCount(),
    );
  }

  // Hits an elastic search endpoint directly and returns the result.
  // The data doesn't get stored in the datastore (at least yet).
  searchLegacy<K extends keyof LegacySearchCollections>(
    key: K,
    query$: SearchQueryParams | Rx.Observable<SearchQueryParams>,
  ): DatastoreSearchCollection<K> {
    const result$ = Rx.combineLatest([
      this.storeBackend.search(key, toObservable(query$)),
      this.auth.authState$,
    ]).pipe(
      map(([result, authState]) =>
        this.searchManager.transform(
          key,
          result,
          authState ? authState.userId : '0',
        ),
      ),
      publishReplay(1),
      refCount(),
    );

    return new DatastoreSearchCollection<K>(
      result$.pipe(map(({ documents: collections }) => collections)),
      result$.pipe(map(({ totalCount }) => totalCount)),
    );
  }

  /* Creates a single object.
   */
  createObject<K extends keyof Collections>(
    collectionName: K,
    object: Collections[K],
    extra?: { readonly [index: string]: string | number },
  ): Promise<BackendPushResponse<K>> {
    return Rx.combineLatest([
      toObservable(collectionName),
      this.auth.authState$,
    ])
      .pipe(
        map(([collection, authState]: [K, AuthState | undefined]) => {
          const path: Path<K> = {
            collection,
            authUid: authState ? authState.userId : LOGGED_OUT_KEY,
          };
          return { path };
        }),
        take(1),
        switchMap(ref => this.storeBackend.push(ref, object, extra)),
      )
      .toPromise();
  }

  private generateClientRequestId() {
    return Math.random()
      .toString(36)
      .substr(2, 16);
  }

  private verifyModuleIsImported(
    collection: keyof Collections,
    state: StoreState,
  ) {
    if (!(collection in state)) {
      throw new DatastoreMissingModuleError(collection);
    }
  }
}
